/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import {
	FloatingToolbarContainer,
	FloatingToolbarContent,
	FloatingToolbarSeparator,
	FloatingToolbarSideContent,
} from '../../common/ui';
import messages from '../messages';

const styles = cssMap({
	skeletonButton: {
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.150'),
	},
	closeIconContainer: {
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
	},
	iconSpacer: {
		width: '26px',
	},
	noIconSpacer: {
		width: '4px',
	},
	invisibleText: {
		height: '0px',
		visibility: 'hidden',
	},
	numberSelected: {
		width: '24px',
		marginRight: token('space.050'),
	},
	selectedContainer: {
		font: token('font.body'),
		height: '0px',
		visibility: 'hidden',
	},
});

type SkeletonBaseProps = {
	width?: string;
};

const SkeletonBase = (props: SkeletonBaseProps) => (
	<Skeleton
		{...props}
		height="7px"
		isShimmering
		color={token('color.background.accent.gray.subtle')}
		ShimmeringEndColor={token('color.background.accent.gray.bolder')}
		width={props.width || '100%'}
		aria-hidden="true"
	/>
);

export type SkeletonActionButtonProps = {
	hasIcon?: boolean;
	text?: string;
	width?: string;
};

const SkeletonActionButton = ({ width, text, hasIcon }: SkeletonActionButtonProps) => {
	return (
		<Box xcss={styles.skeletonButton} aria-hidden="true" testId="floating-toolbar.ui.skeleton.box">
			<SkeletonBase width={width} />
			<Inline>
				<Box xcss={styles.invisibleText}>{text}</Box>
				<Box xcss={hasIcon ? styles.iconSpacer : styles.noIconSpacer} />
			</Inline>
		</Box>
	);
};

const SkeletonCloseIcon = () => (
	<Box xcss={styles.closeIconContainer}>
		<SkeletonBase width="16px" />
	</Box>
);

// These props are used to calculate a more appropriate skeleton
export type SkeletonToolbarProps = {
	buttons: SkeletonActionButtonProps[];
	hasSelectAllButton: boolean;
};

// TODO this needs to be made responsive
export const ToolbarSkeleton = ({
	hasSelectAllButton,
	buttons,
}: SkeletonToolbarProps): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<FloatingToolbarContainer>
			<FloatingToolbarContent>
				<FloatingToolbarSideContent>
					<Box>
						<SkeletonBase />
						<Inline space="space.050" aria-hidden="true">
							<Inline
								xcss={styles.selectedContainer}
								alignBlock="center"
								alignInline="center"
								shouldWrap={false}
							>
								<FormattedMessage
									{...messages.selectedItemsLabel}
									values={{
										numberOfSelectedItems: 1,
										numberSelected: <Box xcss={styles.numberSelected}>{1}</Box>,
									}}
								/>
							</Inline>
						</Inline>
					</Box>
				</FloatingToolbarSideContent>
				{hasSelectAllButton && (
					<SkeletonActionButton text={formatMessage(messages.selectAllButtonLabel)} hasIcon />
				)}
				<FloatingToolbarSeparator />
				<Inline alignBlock="center">
					{buttons.map((button) => {
						return <SkeletonActionButton key={button.text} {...button} />;
					})}
					<SkeletonCloseIcon />
				</Inline>
			</FloatingToolbarContent>
		</FloatingToolbarContainer>
	);
};
